import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { eventSpecializationsService } from '../../shared/services/catalog.service';
import { providersService } from '../../shared/services/providers.service';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import type {
  EventSpecializationRef,
} from '../../shared/types';

type Props = {
  selected: EventSpecializationRef[];
};

/**
 * Read + edit affordance for the provider's event specializations
 * (Wedding, Birthday, Traditional Ceremony, ...). Specializations are
 * provider-controlled; the platform never auto-fills them.
 */
export function EventSpecializationsCard({ selected }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const [open, setOpen] = useState(false);

  const initialIds = useMemo(() => selected.map((s) => s.id), [selected]);
  const [draft, setDraft] = useState<Set<string>>(new Set(initialIds));

  // Reset draft whenever the dialog re-opens, so unsaved edits don't leak
  // back in.
  useEffect(() => {
    if (open) setDraft(new Set(initialIds));
  }, [open, initialIds]);

  const list = useQuery({
    queryKey: ['eventSpecializations'],
    queryFn: () => eventSpecializationsService.list(),
    enabled: open, // Only fetch the catalog when the user opens the editor.
  });

  const save = useMutation({
    mutationFn: () => providersService.updateMySpecializations(Array.from(draft)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['provider', 'me'] });
      notify.success(t('common.save'));
      setOpen(false);
    },
    onError: (err) => notify.error(translate(err)),
  });

  const toggle = (id: string) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: 2, mt: 1 }}
      >
        <Typography variant="h5" sx={{ flex: 1 }}>
          {t('provider.specializationsTitle')}
        </Typography>
        <Button
          startIcon={<EditIcon />}
          variant="outlined"
          size="small"
          onClick={() => setOpen(true)}
          sx={{
            whiteSpace: 'nowrap',
            alignSelf: { xs: 'flex-start', sm: 'auto' },
          }}
        >
          {selected.length === 0
            ? t('provider.specializationsAdd')
            : t('common.edit')}
        </Button>
      </Stack>

      {selected.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 4, borderRadius: 3, borderStyle: 'dashed', textAlign: 'center', mb: 4 }}
        >
          <Typography color="text.secondary">
            {t('provider.specializationsEmpty')}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {selected.map((s) => (
              <Chip
                key={s.id}
                label={s.name}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            ))}
          </Stack>
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" component="span">
            {t('provider.specializationsTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('provider.specializationsHint')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {list.isLoading ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              {t('common.loading')}
            </Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ pt: 1 }}>
              {(list.data ?? []).map((s) => {
                const checked = draft.has(s.id);
                return (
                  <Chip
                    key={s.id}
                    label={s.name}
                    onClick={() => toggle(s.id)}
                    color={checked ? 'primary' : 'default'}
                    variant={checked ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 500 }}
                  />
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? t('common.loading') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
